/**
 * One open document, written as in Word: the ribbon above, the headings on the left, the
 * sheets of paper in the middle (ProseMirror, laid out on the page's paper by the print layout
 * plugin), the template's properties on the right and the status line below. Saved two seconds
 * after the last change (`saver.ts`); someone else's save is shown with the caret kept. Pictures
 * dropped from the computer, pasted, or dragged from the Assets drawer (`ASSET_DROP`) land where
 * they were let go, placed as Word places them (`picture.ts`). Light or dark apart from the app
 * (`look.ts`).
 */
import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import type { Page } from "@protocol/Page";
import type { PageLayout } from "@protocol/PageLayout";
import { EditorState, NodeSelection, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { useApp } from "../../../app/context";
import { ASSET_DROP, type AssetDrop } from "../../../shared/assets";
import { toast } from "../../../shared/toast";
import { blocksOf, docOf, docSchema, sheetOf, textOf, wordCount, type Pagination } from "../../../state/documents";
import { conflictsOf, insertImagesAt } from "./commands";
import { setDocLook, shownLook } from "./look";
import { Navigation } from "./Navigation";
import { editorPlugins, relayout } from "./plugins";
import { pictureBehind, pictureView } from "./picture";
import { printPage } from "./print";
import { Properties } from "./Properties";
import { Ribbon } from "./Ribbon";
import { createSaver } from "./saver";
import { DEFAULT_LAYOUT } from "./constants";

/** What the parts around the paper are given. */
export interface EditorParts {
  /** The editor (`null` until the page is read). */
  view: () => EditorView | null;
  /** Bumped on every change and caret move, so the parts follow the editor. */
  tick: () => number;
  /** The page's paper. */
  layout: () => PageLayout;
  /** Changes the paper. */
  setLayout(layout: PageLayout): void;
  /** The template's parameters' values. */
  params: () => Record<string, string>;
  /** Changes one parameter. */
  setParam(id: string, value: string): void;
  /** The page's template, by id. */
  template: () => string | null;
  /** Whether this person may write. */
  canEdit: () => boolean;
  /** Prints the document (or saves it as a PDF). */
  print(): void;
  /** Keeps what the page says as a template named `name`. */
  saveTemplate(name: string): Promise<boolean>;
  /** The node the page belongs to, if any. */
  node: () => Node | null;
  /** Back to the start screen (every document, the templates). */
  back(): void;
}

/** Whether `file` is a picture the editor shows. */
const isPicture = (file: File) => file.type.startsWith("image/");

/** Where pictures let go at page point `x`, `y` go: beside the top-level block there, before it when the point is in its upper half. */
function dropPlace(view: EditorView, x: number, y: number): { pos: number; before: boolean } {
  const found = view.posAtCoords({ left: x, top: y });
  if (!found) return { pos: view.state.doc.content.size, before: false };
  const $pos = view.state.doc.resolve(found.pos);
  const index = Math.min($pos.index(0), view.state.doc.childCount - 1);
  let start = 0;
  for (let i = 0; i < index; i++) start += view.state.doc.child(i).nodeSize;
  const dom = view.nodeDOM(start);
  const r = dom instanceof HTMLElement ? dom.getBoundingClientRect() : null;
  return { pos: start + 1, before: !!r && y < r.top + r.height / 2 };
}

/** The editor of page `path` (of node `node`, when it has one). */
export function Editor(props: { path: string; node: Node | null; back: () => void }): JSX.Element {
  const { documents, assets, access, presence, pages, theme } = useApp();
  const look = shownLook(theme.mode);
  let mount!: HTMLDivElement;
  const [view, setView] = createSignal<EditorView | null>(null);
  const [tick, setTick] = createSignal(0);
  const [layout, setLayoutSignal] = createSignal<PageLayout>(DEFAULT_LAYOUT);
  const [params, setParams] = createSignal<Record<string, string>>({});
  const [template, setTemplate] = createSignal<string | null>(null);
  const [laidOut, setLaidOut] = createSignal<Pagination>({ breaks: [], sheets: 1, tail: 0 });
  const [missing, setMissing] = createSignal(false);
  const [conflicts, setConflicts] = createSignal<string[]>([]);
  const canEdit = () => access.can("editor");
  const sheet = createMemo(() => sheetOf(layout()));
  let version = 1;
  /** The pictures shown, refreshed when a file is read. */
  const pictures = new Set<() => void>();
  const [picturesAdded, setPicturesAdded] = createSignal(0);

  /** The page as the editor holds it now. */
  const current = (): Page => {
    const v = view();
    return { version, template: template() ?? undefined, params: params(), layout: layout(), blocks: v ? blocksOf(v.state.doc) : [] };
  };

  /** Shows `page`: its words (the caret kept where it was), its parameters and paper. */
  const apply = (page: Page) => {
    version = page.version;
    setTemplate(page.template ?? null);
    setParams({ ...page.params });
    const paperChanged = JSON.stringify(page.layout) !== JSON.stringify(layout());
    setLayoutSignal(page.layout ?? DEFAULT_LAYOUT);
    const v = view();
    if (!v) return;
    const doc = docOf(page);
    const { from, to } = v.state.selection;
    const tr = v.state.tr.replaceWith(0, v.state.doc.content.size, doc.content);
    const size = tr.doc.content.size;
    try {
      tr.setSelection(TextSelection.create(tr.doc, Math.min(from, size), Math.min(to, size)));
    } catch {
      tr.setSelection(TextSelection.atStart(tr.doc));
    }
    v.dispatch(tr.setMeta("addToHistory", false).setMeta("remote", true));
    if (paperChanged) relayout(v);
  };

  const saver = createSaver({
    path: props.path,
    documents,
    you: presence.you,
    current,
    apply,
    conflicts: setConflicts,
    canSave: canEdit,
  });

  const changed = () => {
    setTick((t) => t + 1);
    saver.touched();
  };

  /** Keeps the computer's picture files with the project and puts them in the document at page point `x`, `y` (the caret when none). */
  const addPictures = async (files: File[], at: { x: number; y: number } | null) => {
    const pictures = files.filter(isPicture);
    if (!pictures.length) return false;
    if (!canEdit()) {
      toast("Editors and owners add pictures to documents.", "error");
      return true;
    }
    const v = view();
    if (!v) return true;
    const where = at ? dropPlace(v, at.x, at.y) : { pos: v.state.selection.from, before: false };
    const kept = await assets.upload("project", pictures, "");
    if (kept.length) insertImagesAt(where.pos, where.before, kept.map((f) => f.path))(v.state, v.dispatch);
    return true;
  };

  /** A file dragged from the Assets drawer: a picture where it was let go (a computer's file copied into the project first). */
  const fromDrawer = async (e: Event) => {
    const drop = (e as CustomEvent<AssetDrop>).detail;
    const v = view();
    if (!v) return;
    if (!canEdit()) return void toast("Editors and owners add pictures to documents.", "error");
    const where = dropPlace(v, drop.x, drop.y);
    const path = drop.source === "project" ? drop.path : (await assets.copy(drop.source, drop.path, "project", ""))?.path;
    if (path) insertImagesAt(where.pos, where.before, [path])(v.state, v.dispatch);
    v.focus();
  };

  onMount(() => {
    void documents.read(props.path).then((page) => {
      if (!page) return void setMissing(true);
      version = page.version;
      setTemplate(page.template ?? null);
      setParams({ ...page.params });
      setLayoutSignal(page.layout ?? DEFAULT_LAYOUT);
      const editor = new EditorView(mount, {
        state: EditorState.create({ schema: docSchema, doc: docOf(page), plugins: editorPlugins(sheet, setLaidOut) }),
        editable: canEdit,
        attributes: { class: "doc-text", id: "doc-text", spellcheck: "true", "aria-label": "Document" },
        nodeViews: {
          image: (node, editorView, getPos) => {
            const shown = pictureView(node, editorView, getPos, { url: (src) => assets.url(src), canEdit, shown: pictures, sheet });
            // After it joined the pictures: the effect below then follows its file.
            queueMicrotask(() => setPicturesAdded((n) => n + 1));
            return shown;
          },
        },
        handleClick(_view, _pos, event) {
          const mention = (event.target as HTMLElement | null)?.closest?.("[data-mention]");
          if (!mention || (!event.ctrlKey && !event.metaKey && canEdit())) return false;
          pages.follow(mention.getAttribute("data-mention") ?? "");
          return true;
        },
        handleDOMEvents: {
          // A picture behind the words is picked where no word is, as in Word.
          mousedown(editorView, event) {
            if (event.button !== 0) return false;
            const figure = pictureBehind(editorView.dom, event.clientX, event.clientY);
            if (!figure) {
              // A press in the words while a picture is selected puts the caret there at once: the
              // picture's own gestures kept the browser's selection from following.
              const sel = editorView.state.selection;
              const target = event.target as HTMLElement | null;
              if (sel instanceof NodeSelection && sel.node.type.name === "image" && !target?.closest?.(".doc-image")) {
                const at = editorView.posAtCoords({ left: event.clientX, top: event.clientY });
                if (at) editorView.dispatch(editorView.state.tr.setSelection(TextSelection.near(editorView.state.doc.resolve(at.pos))));
              }
              return false;
            }
            const pos = editorView.posAtDOM(figure, 0);
            const node = editorView.state.doc.nodeAt(pos);
            if (node?.type.name !== "image") return false;
            event.preventDefault();
            editorView.dispatch(editorView.state.tr.setSelection(NodeSelection.create(editorView.state.doc, pos)));
            editorView.focus();
            figure.querySelector(".doc-image-frame")?.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true, clientX: event.clientX, clientY: event.clientY, button: 0 }));
            return true;
          },
        },
        handleDrop(_view, event, _slice, moved) {
          const files = [...(event.dataTransfer?.files ?? [])];
          if (moved || !files.some(isPicture)) return false;
          // Taken here: the graph window makes no node of them.
          event.preventDefault();
          void addPictures(files, { x: event.clientX, y: event.clientY });
          return true;
        },
        handlePaste(_view, event) {
          const files = [...(event.clipboardData?.files ?? [])];
          if (!files.some(isPicture)) return false;
          void addPictures(files, null);
          return true;
        },
        dispatchTransaction(tr) {
          const next = editor.state.apply(tr);
          editor.updateState(next);
          setTick((t) => t + 1);
          if (tr.docChanged && !tr.getMeta("remote")) saver.touched();
        },
      });
      saver.start(page);
      setView(editor);
      setTick((t) => t + 1);
    });
  });
  onCleanup(() => {
    saver.stop();
    view()?.destroy();
  });
  let root!: HTMLDivElement;
  onMount(() => root.addEventListener(ASSET_DROP, fromDrawer));
  onCleanup(() => root.removeEventListener(ASSET_DROP, fromDrawer));

  // Pictures follow their files (read once per change).
  createEffect(() => {
    picturesAdded();
    for (const refresh of pictures) refresh();
  });
  // Someone may stop being an editor (or become one).
  createEffect(() => {
    canEdit();
    view()?.setProps({ editable: canEdit });
  });

  const setLayout = (next: PageLayout) => {
    setLayoutSignal(next);
    const v = view();
    if (v) relayout(v);
    changed();
  };

  const words = createMemo(() => {
    tick();
    const v = view();
    return v ? wordCount(textOf(v.state.doc)) : 0;
  });
  const conflicted = createMemo(() => {
    tick();
    const v = view();
    return v ? conflictsOf(v.state.doc).length : 0;
  });

  const parts: EditorParts = {
    view,
    tick,
    layout,
    setLayout,
    params,
    setParam(id, value) {
      setParams((p) => ({ ...p, [id]: value }));
      changed();
    },
    template,
    canEdit,
    print: () => {
      const v = view();
      if (v) printPage(v.dom, layout(), props.node?.name ?? "Document");
    },
    async saveTemplate(name) {
      const id = name.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "");
      if (!id) return false;
      const from = documents.template(template());
      return documents.saveTemplate({ id, name: name.trim(), summary: from?.summary ?? "", kind: from?.kind ?? props.node?.kind ?? "note", params: from?.params ?? [], blocks: current().blocks });
    },
    node: () => props.node,
    back: () => props.back(),
  };

  const statusWords: Record<string, string> = { saved: "Saved", unsaved: "Not saved yet", saving: "Saving…", refused: "Not saved: the core refused it" };

  return (
    <div ref={root} class="doc" id="doc-editor" data-doc-path={props.path} data-sheets={laidOut().sheets} data-doc-look={look()} data-asset-target="document">
      <Ribbon parts={parts} />
      <Show when={!missing()} fallback={<div class="doc-missing muted">This document could not be read. <button type="button" class="btn" onClick={() => props.back()}>Back to the documents</button></div>}>
        <div class="doc-body">
          <Navigation parts={parts} />
          <div class="doc-desk">
            <div
              class="doc-paper"
              style={{
                width: `${sheet().width}px`,
                "min-height": `${sheet().height}px`,
                padding: `${sheet().margins[0]}px ${sheet().margins[1]}px ${laidOut().tail + sheet().margins[2]}px ${sheet().margins[3]}px`,
              }}
            >
              {/* A picture is never taller than a sheet's room for text. */}
              <div ref={mount} class="doc-mount" style={{ "--sheet-content": `${sheet().content}px` }} />
              <span class="doc-page-number last" style={{ bottom: `${Math.max(8, sheet().margins[2] / 2 - 8)}px` }}>
                {laidOut().sheets}
              </span>
            </div>
          </div>
          <Properties parts={parts} />
        </div>
      </Show>
      <div class="doc-status" id="doc-status">
        <span id="doc-pages">
          Page{laidOut().sheets === 1 ? "" : "s"}: {laidOut().sheets}
        </span>
        <span id="doc-words">{words()} words</span>
        <Show when={conflicted() || conflicts().length}>
          <span class="doc-status-conflicts" id="doc-conflicts">
            {conflicted()} to settle (Review)
          </span>
        </Show>
        <button
          type="button"
          class="doc-look-toggle"
          id="doc-look-toggle"
          title={look() === "dark" ? "Light page (View → Page colour)" : "Dark page (View → Page colour)"}
          aria-label={look() === "dark" ? "Light page" : "Dark page"}
          onClick={() => setDocLook(look() === "dark" ? "light" : "dark")}
        >
          {look() === "dark" ? "☀" : "☾"}
        </button>
        <span class="doc-status-save" id="doc-save-status" data-status={saver.status()}>
          {canEdit() ? statusWords[saver.status()] : "Read only"}
        </span>
        <For each={presence.others().filter((p) => p.selection?.kind === "node" && p.selection.id === props.node?.id)}>
          {(person) => (
            <span class="doc-person" style={{ "--person": person.colour }} title={`${person.name} is on this page`}>
              {person.name}
            </span>
          )}
        </For>
      </div>
    </div>
  );
}
